// Calendar utility functions

export const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    const prevMonth = new Date(year, month - 1, 0);
    const daysInPrevMonth = prevMonth.getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
        days.push(new Date(year, month - 1, daysInPrevMonth - i));
    }

    for (let day = 1; day <= daysInMonth; day++) {
        days.push(new Date(year, month, day));
    }

    const remainingCells = 42 - days.length;
    for (let day = 1; day <= remainingCells; day++) {
        days.push(new Date(year, month + 1, day));
    }

    return days;
};

/** Sunday-start week containing `date` */
export const getWeekDays = (date: Date) => {
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - start.getDay());

    return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(start);
        d.setDate(start.getDate() + i);
        return d;
    });
};

export const HOUR_HEIGHT = 52;
export const DAY_START_HOUR = 0;
export const DAY_END_HOUR = 24;

export const getHourSlots = () =>
    Array.from({ length: DAY_END_HOUR - DAY_START_HOUR }, (_, i) => DAY_START_HOUR + i);

export const formatHourLabel = (hour: number) => {
    if (hour === 0) return "12 AM";
    if (hour === 12) return "12 PM";
    if (hour < 12) return `${hour} AM`;
    return `${hour - 12} PM`;
};

/** Minutes from midnight; null when all-day / untimed */
export const getEventStartMinutes = (time?: string, allDay?: boolean) => {
    if (allDay || !time) return null;
    const [h, m] = time.split(":").map(Number);
    if (Number.isNaN(h)) return null;
    return h * 60 + (Number.isNaN(m) ? 0 : m);
};

export const minutesToTop = (minutes: number) =>
    ((minutes - DAY_START_HOUR * 60) / 60) * HOUR_HEIGHT;

export const formatDate = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
};

export const isCurrentMonth = (date: Date, currentDate: Date) => {
    return (
        date.getMonth() === currentDate.getMonth() &&
        date.getFullYear() === currentDate.getFullYear()
    );
};

export const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
};

export const isSelected = (date: Date, selectedDate: Date) => {
    return date.toDateString() === selectedDate.toDateString();
};

export const addDays = (date: Date, days: number) => {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
};

export const getViewTitle = (date: Date, view: "day" | "week" | "month") => {
    if (view === "day") {
        return date.toLocaleDateString("en-US", {
            weekday: "long",
            month: "long",
            day: "numeric",
            year: "numeric",
        });
    }

    if (view === "week") {
        const days = getWeekDays(date);
        const start = days[0];
        const end = days[6];
        const sameMonth = start.getMonth() === end.getMonth();
        const sameYear = start.getFullYear() === end.getFullYear();

        if (sameMonth) {
            return `${start.toLocaleDateString("en-US", { month: "long" })} ${start.getDate()}–${end.getDate()}, ${start.getFullYear()}`;
        }

        if (sameYear) {
            return `${start.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${end.toLocaleDateString("en-US", { month: "short", day: "numeric" })}, ${start.getFullYear()}`;
        }

        return `${start.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} – ${end.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
    }

    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

export const getEventColor = (
    color: string,
    mode: "light" | "dark" | "system" = "light",
    accentColors?: Array<{ Name: string; LightHex: string; DarkHex: string }>,
) => {
    if (accentColors?.length) {
        const match = accentColors.find(
            (c) =>
                c.Name?.trim().toLowerCase() ===
                String(color || "").trim().toLowerCase(),
        );
        if (match) {
            return mode === "dark" ? match.DarkHex : match.LightHex;
        }
    }

    const lightColorMap: Record<string, string> = {
        purple: "#e9d5ff",
        indigo: "#c7d2fe",
        violet: "#ddd6fe",
        red: "#fecaca",
        green: "#bbf7d0",
        blue: "#bfdbfe",
        orange: "#fed7aa",
        yellow: "#fef3c7",
    };

    const darkColorMap: Record<string, string> = {
        purple: "#4c1d95",
        indigo: "#312e81",
        violet: "#581c87",
        red: "#991b1b",
        green: "#14532d",
        blue: "#1e3a8a",
        orange: "#9a3412",
        yellow: "#92400e",
    };

    return mode === "dark" ? darkColorMap[color] || "#4c1d95" : lightColorMap[color] || "#e9d5ff";
};

export const getEventTextColor = (
    color: string,
    mode: "light" | "dark" | "system" = "light",
    accentColors?: Array<{ Name: string; LightHex: string; DarkHex: string }>,
) => {
    if (accentColors?.length) {
        const match = accentColors.find(
            (c) =>
                c.Name?.trim().toLowerCase() ===
                String(color || "").trim().toLowerCase(),
        );
        if (match) {
            return mode === "dark" ? match.LightHex : match.DarkHex;
        }
    }

    const lightTextColorMap: Record<string, string> = {
        purple: "#7c3aed",
        indigo: "#4338ca",
        violet: "#7c2d12",
        red: "#dc2626",
        green: "#16a34a",
        blue: "#2563eb",
        orange: "#ea580c",
        yellow: "#d97706",
    };

    const darkTextColorMap: Record<string, string> = {
        purple: "#c4b5fd",
        indigo: "#a5b4fc",
        violet: "#c084fc",
        red: "#fca5a5",
        green: "#86efac",
        blue: "#93c5fd",
        orange: "#fdba74",
        yellow: "#fde047",
    };

    return mode === "dark"
        ? darkTextColorMap[color] || "#c4b5fd"
        : lightTextColorMap[color] || "#7c3aed";
};

export const getStatusColor = (status: string, mode: "light" | "dark" | "system" = "light") => {
    const lightStatusColorMap: Record<string, string> = {
        "NOT-STARTED": "#6b7280",
        "IN-PROGRESS": "#3b82f6",
        "IN-REVIEW": "#f59e0b",
        COMPLETED: "#10b981",
        CANCELLED: "#B0BEC5",
    };

    const darkStatusColorMap: Record<string, string> = {
        "NOT-STARTED": "#9ca3af",
        "IN-PROGRESS": "#60a5fa",
        "IN-REVIEW": "#fbbf24",
        COMPLETED: "#34d399",
        CANCELLED: "#d1d5db",
    };

    return mode === "dark"
        ? darkStatusColorMap[status] || "#9ca3af"
        : lightStatusColorMap[status] || "#6b7280";
};

export const getStatusName = (status: string) => {
    const statusNameMap: Record<string, string> = {
        "NOT-STARTED": "Not Started",
        "IN-PROGRESS": "In Progress",
        "IN-REVIEW": "In Review",
        COMPLETED: "Completed",
        CANCELLED: "Cancelled",
    };
    return statusNameMap[status] || "Unknown";
};
